import { ApiClient } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';

export function extractSubscriptionChargeUrl(response: unknown): string {
  const data = (response as { data?: Record<string, unknown> })?.data;
  const subscription = data?.subscription as Record<string, unknown> | undefined;
  const candidates = [
    subscription?.charge_url,
    data?.charge_url,
    data?.payment_url,
    subscription?.payment_url,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }
  return '';
}

export function extractCustomerSubscriptionId(response: unknown): number | null {
  const data = (response as { data?: Record<string, unknown> })?.data;
  const subscription = data?.subscription as Record<string, unknown> | undefined;
  const raw = subscription?.id ?? data?.id ?? data?.customer_subscription_id;
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function subscription_record(response: unknown): Record<string, unknown> | null {
  const data = (response as { data?: Record<string, unknown> })?.data;
  const subscription = data?.subscription;
  if (subscription != null && typeof subscription === 'object') {
    return subscription as Record<string, unknown>;
  }
  return null;
}

/**
 * True when checkout already completed payment (free plan / 100% coupon / points / already captured).
 * In that case there is no charge URL to open — skip generate/charge_url and leave checkout.
 */
export function isSubscriptionCheckoutPaymentSettled(response: unknown): boolean {
  if (extractSubscriptionChargeUrl(response)) {
    return false;
  }
  const sub = subscription_record(response);
  if (!sub) {
    return false;
  }

  const payment_status = String(sub.payment_status ?? '')
    .trim()
    .toLowerCase();
  if (['captured', 'paid', 'success', 'completed', 'free'].includes(payment_status)) {
    return true;
  }

  const final_cost = Number(sub.final_cost);
  if (Number.isFinite(final_cost) && final_cost <= 0) {
    return true;
  }

  return false;
}

export async function fetchSubscriptionPaymentUrl(args: {
  customer_id: number;
  customer_subscription_id: number | string;
  payment_gateway: string;
}): Promise<string | null> {
  const params = {
    customer_id: args.customer_id,
    customer_subscription_id: args.customer_subscription_id,
    payment_gateway: args.payment_gateway,
  };

  /** Backend route supports GET only (`customer_subscriptions/generate/charge_url`). */
  const response: unknown = await ApiClient.get(endpoints.generatePayURL, params);

  const payment_url = extractSubscriptionChargeUrl(response);
  if (payment_url) {
    return payment_url;
  }

  return null;
}

export async function resolveSubscriptionCheckoutPaymentUrl(args: {
  checkout_response: unknown;
  customer_id: number;
  payment_gateway: string;
}): Promise<{ charge_url: string; customer_subscription_id: number | null; payment_settled: boolean }> {
  const from_checkout = extractSubscriptionChargeUrl(args.checkout_response);
  const customer_subscription_id = extractCustomerSubscriptionId(args.checkout_response);

  if (from_checkout) {
    return { charge_url: from_checkout, customer_subscription_id, payment_settled: false };
  }

  if (isSubscriptionCheckoutPaymentSettled(args.checkout_response)) {
    return { charge_url: '', customer_subscription_id, payment_settled: true };
  }

  if (!customer_subscription_id || !args.payment_gateway) {
    return { charge_url: '', customer_subscription_id, payment_settled: false };
  }

  try {
    const generated = await fetchSubscriptionPaymentUrl({
      customer_id: args.customer_id,
      customer_subscription_id,
      payment_gateway: args.payment_gateway,
    });
    return { charge_url: generated ?? '', customer_subscription_id, payment_settled: false };
  } catch {
    return { charge_url: '', customer_subscription_id, payment_settled: false };
  }
}
